import { ShieldAlert } from 'lucide-react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Text } from '@/components/ui/typography';

export class RouterError extends Error {
  statusText?: string;

  status: number;

  override message: string;

  override stack?: string;

  config?: unknown;

  code?: string;

  request?: unknown;

  response?: unknown;

  isAxiosError?: boolean;

  constructor(message: string, statusCode: number) {
    super(message);
    this.status = statusCode;
    this.message = message;
  }

  toJSON = (): RouterError => {
    return {
      message: this.message,
      status: this.status,
      name: this.name,
      stack: this.stack,
      config: this.config,
      code: this.code,
      request: this.request,
      response: this.response,
      isAxiosError: this.isAxiosError,
      toJSON: this.toJSON,
    };
  };
}

interface ErrorPageProps {
  message?: string;
}

/**
 * Get the required codes from the route error
 * @param data
 * @returns
 * @description `requirePermissions` sends the codes it asked for; only useful to whoever is
 * wiring the gates, so they stay behind the DEV flag.
 */
const requiredCodes = (data: unknown) =>
  (data as { required?: { oneOf?: string[] } } | undefined)?.required?.oneOf ??
  [];

const StatusAlert = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center justify-center min-h-[50vh] p-4">
    <div className="max-w-md w-full">
      <Alert variant="destructive">
        <ShieldAlert />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>{children}</AlertDescription>
      </Alert>
    </div>
  </div>
);

const ErrorPage = (props: ErrorPageProps) => {
  const routeError = useRouteError();

  if (isRouteErrorResponse(routeError)) {
    if (routeError.status === 403) {
      const codes = requiredCodes(routeError.data);

      return (
        <StatusAlert title="You are not allowed to open this page">
          <p>
            This page needs a permission your Roles do not grant on this Game or
            Sandbox. Ask a Permission Manager of this Game for access.
          </p>
          {import.meta.env.DEV && codes.length > 0 && (
            <p className="mt-2 font-mono text-xs">
              Requires one of: {codes.join(', ')}
            </p>
          )}
        </StatusAlert>
      );
    }

    if (routeError.status === 404) {
      // Thrown by React Router itself
      return (
        <div className="flex flex-1 w-full min-h-[60vh] flex-col items-center justify-center gap-2 p-4 text-center">
          <span
            className="text-8xl font-bold leading-none text-muted-foreground"
            aria-hidden
          >
            404
          </span>
          <Text variant="title" as="h1">
            This page does not exist
          </Text>
          <Text variant="muted">
            The address is wrong or the page has moved.
          </Text>
        </div>
      );
    }
  }

  const error = (routeError as RouterError | undefined) ?? {
    message: props.message,
    statusText: '',
  };

  return (
    <div id="error-page">
      <h1>Oops!</h1>
      <p>Sorry, an unexpected error has occurred.</p>
      <p>
        <i>
          {error.statusText} {error.message}
        </i>
      </p>
    </div>
  );
};

export default ErrorPage;
